/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { isBoolean, isString, forOwn } from "utils/lodashUtils";
import { LINEARIZATION_DEFAULT_BLOCK_SIZE, FALLBACK_TIMEOUT } from "constants/constants";

const toBoolean = (value, defaultValue) => {
  if (isBoolean(value)) return value;
  if (isString(value)) return value.toLowerCase() === "true";
  return defaultValue;
};

export const isHexColor = (value, defaultValue) => {
  var isOk = /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i.test(value);
  if (isOk) return value;
  return defaultValue;
};

const identity = value => value;

const isPromise = (value, defaultValue) => {
  if (typeof value === "object" && typeof value.then === "function") {
    return value;
  }
  return defaultValue;
};

const isPositiveInteger = (value, defaultValue) => (Number.isInteger(value) && value > 0 ? value : defaultValue);

const config = {
  showLHP: { default: undefined /* true */, validator: toBoolean },
  fixedWidthLeftMargin: { default: undefined, validator: identity },
  fixedWidthRightMargin: { default: undefined, validator: identity },
  shouldRenderPersonalCommentsDropin: { default: undefined, validator: identity },
  shouldRenderCommentsDropin: { default: false, validator: toBoolean },
  shouldRenderSignAuthoringDropin: { default: false, validator: toBoolean },
  shouldRenderHighlightsDropin: { default: false, validator: toBoolean },
  shouldRenderPageShadows: { default: false, validator: toBoolean },
  shouldRenderVisualizerDropin: { default: false, validator: toBoolean },
  shouldRenderESignOverlayDropin: { default: false, validator: toBoolean },
  shouldDisableDownloadInContextMenu: { default: false, validator: toBoolean },
  shouldDocumentTakeFocus: { default: false, validator: toBoolean },
  docViewBgColorBeforeFileLoad: { default: "var(--docViewBgColorBeforeFileLoad, #FFFFFF)", validator: isHexColor },
  docViewBgColorAfterFileLoad: { default: "var(--docViewBgColorAfterFileLoad, #F5F5F5)", validator: isHexColor },
  alternateAssetUri: { default: "", validator: identity },
  assetUri: { default: "", validator: identity },
  path: { default: "", validator: identity },
  filetype: { default: "", validator: identity },
  shouldDockHUD: { default: undefined /* true */, validator: toBoolean },
  shouldShowDownloadButtonInHud: { default: false, validator: toBoolean },
  enableAccessibilityByDefault: { default: false, validator: toBoolean },
  shouldShowSearchInHud: { default: false, validator: toBoolean },
  docTitle: { default: undefined, validator: identity },
  disableContextMenuAutoTrigger: { default: false, validator: toBoolean },
  enableReactVirtualized: { default: true, validator: toBoolean },
  selectivelyRenderAnnotsFlag: { default: undefined, validator: identity },
  shouldShowUndoButtonInLHP: { default: undefined, validator: identity },
  shouldShowRedoButtonInLHP: { default: undefined, validator: identity },
  shouldRenderAcroForm: { default: false, validator: toBoolean },
  context: { default: "", validator: identity },
  enableLowResolutionRendition: { default: true, validator: toBoolean },
  enableLinearization: { default: false, validator: toBoolean },
  editDeepLink: { default: false, validator: toBoolean },
  enableFillSign: { default: true, validator: toBoolean },
  enablePdfRequestSignatures: { default: false, validator: toBoolean },
  forceSetExternalMetadata: { default: false, validator: toBoolean },
  useViewSDKForAcroformSubmit: { default: false, validator: toBoolean },
  caretMode: { default: false, validator: toBoolean },
  scrollToShowCaret: { default: false, validator: toBoolean },
  showCaretOnPDFOpen: { default: false, validator: toBoolean },
  enableEncryptedWrite: { default: false, validator: toBoolean },
  suppressPasswordDialog: { default: false, validator: toBoolean },
  fallbackPrintTimeout: { default: FALLBACK_TIMEOUT, validator: identity },
  enablePDFJavaScriptOverridePromise: { default: Promise.resolve(false), validator: isPromise },
  linearizationBlockSize: { default: LINEARIZATION_DEFAULT_BLOCK_SIZE, validator: isPositiveInteger },
  fillSignConfig: { default: {}, validator: identity },
  showPremiumIconForNonDCWebClients: { default: false, validator: toBoolean },
  filePath: { default: undefined, validator: identity },
  hideStickyToolOnTap: { default: false, validator: toBoolean },
  showAssistantInRHP: { default: false, validator: toBoolean },
  enableRHPResizing: { default: false, validator: toBoolean },
  enableInlineOrganize: { default: false, validator: toBoolean },
  disableProtectedPDFCancelButton: { default: false, validator: toBoolean },
  useNewThumbnailsGrid: { default: false, validator: toBoolean },
  shouldDeferLoadOverlays: { default: false, validator: toBoolean },
  rhpSkeletonDropinId: { default: "", validator: identity },
  bottomBannerConfig: { default: {}, validator: identity },
  renderNotificationBannerBeforeDoc: { default: false, validator: toBoolean }
};

const getDefaultConfig = () => {
  let defaultConfig = {};
  forOwn(config, (value, key) => {
    defaultConfig[key] = value.default;
  });
  return defaultConfig;
};

const getConfig = params => {
  let finalConfig = {};
  forOwn(params, (value, key) => {
    if (config.hasOwnProperty(key)) {
      finalConfig[key] = config[key].validator(value, config[key].default);
    }
  });
  return finalConfig;
};

export { getDefaultConfig, getConfig };
